<script setup lang="ts">
import { mdiPencil, mdiGithub } from '@mdi/js'
import type { CreatorPublic } from '~/composables/api/useUserApi'

// X (Twitter) のロゴ SVG パス。`WorkPlay.vue` でも同じものを使用している。
const xLogoIcon = 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.07l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z'

definePageMeta({
  layout: 'landing',
})

const route = useRoute()
const runtimeConfig = useRuntimeConfig()
const authInfoStore = useAuthInfoStore()

const slug = computed(() => {
  const raw = route.params.slug
  return Array.isArray(raw) ? (raw[0] ?? '').toLowerCase() : (raw ?? '').toLowerCase()
})

// SSR fetch — 404 は createError() で error.vue に振り替え (#417)。
// useAsyncData の handler 内で throw すると Nuxt が error として保持するだけで
// error.vue まで自動伝播しないので、`error` を受け取った上でページレベルで
// 改めて throw する。作品一覧の liked は認証ユーザー基準（#751 でいいねをログイン
// 必須に戻したため、未ログイン viewer は liked=false）。
const { data: creator, error: creatorError } = await useAsyncData(
  () => `creator-${slug.value}`,
  () => $fetch<CreatorPublic>(
    `${runtimeConfig.public.apiBase}/api/v1/public/creators/${slug.value}`,
  ),
)
if (creatorError.value || !creator.value) {
  throw createError({
    statusCode: 404,
    statusMessage: `クリエイター "${slug.value}" は見つかりませんでした`,
    fatal: true,
  })
}

const defaultOgImage = `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`

useSeoMeta({
  title: () => creator.value
    ? `${creator.value.name} の Pyxel 作品 | Code & Magic`
    : 'Code & Magic',
  description: () => creator.value
    ? `${creator.value.name} が公開している Pyxel 作品 ${creator.value.totalWorks} 件のポートフォリオ。`
    : '指定されたクリエイターページは存在しません。',
  ogTitle: () => creator.value
    ? `${creator.value.name} の Pyxel 作品ポートフォリオ`
    : 'Code & Magic',
  ogImage: defaultOgImage,
  twitterCard: 'summary_large_image',
})

// #803: 51 件目以降をスクロールで追記する。1 ページ目は creator レスポンスに
// 同梱されて SSR HTML に載っているので、ここが呼ばれるのは 2 ページ目から。
//
// **SSR 初回ページと同じ件数でなければならない**。ここがズレると「page 2」の
// 指す範囲が SSR 済みの範囲と食い違い、取りこぼし（または無駄な重複取得）になる。
// 正本はバックエンドの `CREATOR_WORKS_SSR_PAGE_SIZE`（`handlers/users.py`）。
const CREATOR_WORKS_PER_PAGE = 50
const { listCreatorWorks } = useApi()

const fetchCreatorWorks = async (page: number, perPage: number) => {
  const resp = await listCreatorWorks(slug.value, page, perPage)
  return { works: resp.works, total: resp.total }
}

// 本人判定 (sub == userId)。ハイドレーション後にのみ確定する。
const isOwner = computed(() =>
  Boolean(creator.value?.userId)
  && creator.value?.userId === authInfoStore.sub,
)

// 編集モーダルは閲覧ページ上では開かず、認証世界 (= 冒険者のキャンプ) に
// 誘導する。サインイン済みならそのままキャンプへ遷移し ?edit=profile で
// ダイアログが自動展開、未サインインなら authenticated middleware が
// /signin?redirect=... に振り直す形でサインインへ誘導する。
const goToEdit = () => navigateTo('/adventurerscamp?edit=profile')

// 冒険になった日 (#417) — User.created_at を年月単位に丸めて表示。
// X (Twitter) / itch.io の Joined since 表記に倣う。
const joinedLabel = computed(() => {
  if (!creator.value?.joinedAt) return ''
  const d = new Date(creator.value.joinedAt)
  if (Number.isNaN(d.getTime())) return ''
  return `${d.getFullYear()}年${d.getMonth() + 1}月から`
})

// 外部リンクの 4 枠のうち、設定されているものだけを順序で出す。
interface LinkChip {
  key: 'x' | 'github' | 'itch' | 'pixiv'
  url: string
}
const externalChips = computed<LinkChip[]>(() => {
  const links = creator.value?.externalLinks
  if (!links) return []
  const out: LinkChip[] = []
  if (links.x) out.push({ key: 'x', url: links.x })
  if (links.github) out.push({ key: 'github', url: links.github })
  if (links.itch) out.push({ key: 'itch', url: links.itch })
  if (links.pixiv) out.push({ key: 'pixiv', url: links.pixiv })
  return out
})
</script>

<template>
  <div class="creator-page">
    <article
      v-if="creator"
      class="creator-card"
    >
      <header class="creator-card__head">
        <div class="creator-card__head-inner">
          <div class="creator-card__title-row">
            <h1 class="creator-card__name">
              {{ creator.name }}
            </h1>
            <span
              v-if="creator.title"
              class="creator-card__title"
            >{{ creator.title }}</span>
            <v-btn
              v-if="isOwner"
              class="creator-card__edit-btn"
              variant="outlined"
              size="small"
              :prepend-icon="mdiPencil"
              @click="goToEdit"
            >
              編集
            </v-btn>
          </div>
          <!-- @handle / 冒険になった日 / 外部リンク を 1 行にまとめる。
               幅が狭ければ flex-wrap で折り返す。外部リンクは
               `margin-left: auto` で右端に寄せる。 -->
          <div class="creator-card__meta">
            <span class="creator-card__handle">@{{ creator.slug }}</span>
            <span
              v-if="joinedLabel"
              class="creator-card__meta-sep"
              aria-hidden="true"
            >・</span>
            <span
              v-if="joinedLabel"
              class="creator-card__joined"
            >{{ joinedLabel }}</span>
            <div
              v-if="externalChips.length"
              class="creator-card__links"
            >
              <a
                v-for="chip in externalChips"
                :key="chip.key"
                :href="chip.url"
                target="_blank"
                rel="noopener me"
                class="link-chip"
                :class="`link-chip--${chip.key}`"
                :title="chip.key === 'x' ? 'X' : chip.key === 'github' ? 'GitHub' : chip.key === 'itch' ? 'itch.io' : 'Pixiv'"
                :aria-label="chip.key === 'x' ? 'X' : chip.key === 'github' ? 'GitHub' : chip.key === 'itch' ? 'itch.io' : 'Pixiv'"
              >
                <svg
                  v-if="chip.key === 'x'"
                  class="link-chip__icon"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path :d="xLogoIcon" />
                </svg>
                <v-icon
                  v-else-if="chip.key === 'github'"
                  class="link-chip__icon"
                  :icon="mdiGithub"
                  aria-hidden="true"
                />
                <span
                  v-else-if="chip.key === 'itch'"
                  class="link-chip__text"
                >itch.io</span>
                <span
                  v-else
                  class="link-chip__text"
                >pixiv</span>
              </a>
            </div>
          </div>
        </div>
      </header>

      <section class="creator-card__body">
        <!-- 自己紹介 (Markdown) — 編集ダイアログのプレビューと同じ
             CreatorBio コンポーネントを使い、見た目を完全に揃える。 -->
        <CreatorBio
          v-if="creator.bioMarkdown"
          :markdown="creator.bioMarkdown"
          class="creator-card__bio"
        />
        <p
          v-else-if="isOwner"
          class="bio-empty"
        >
          自己紹介はまだ設定されていません。「編集」から書き加えられます。
        </p>

        <!-- 外部リンクはヘッダーに移動 (#417) -->

        <!-- 作品グリッド — トップページの作品ギャラリーと同じ動き:
             interactive=false でクリック導線を play 一本にし、
             play で /works/<slug> へ navigate する。 -->
        <div class="works">
          <h2 class="works__heading">
            作品 ({{ creator.totalWorks }})
          </h2>
          <!-- #803: 1 ページ目は SSR 同梱（`initialItems`）、51 件目以降だけを
               スクロールで追記する。以前は 50 件固定で、それを超える作者の作品が
               見出しの件数とだけ食い違ってサイレントに消えていた。 -->
          <WorkGallery
            :fetcher="fetchCreatorWorks"
            :per-page="CREATOR_WORKS_PER_PAGE"
            :initial-items="creator.works"
            :initial-total="creator.totalWorks"
          >
            <template #empty>
              <div class="works__empty">
                公開されている作品はまだありません。
              </div>
            </template>

            <template #default="{ works }">
              <div class="works__grid">
                <WorkCard
                  v-for="work in works"
                  :key="work.workId"
                  :work="work"
                  :interactive="false"
                  @play="navigateTo(work.publicUrl || '')"
                />
              </div>
            </template>
          </WorkGallery>
        </div>
      </section>

      <footer class="creator-card__foot">
        <a
          class="creator-card__credit-link"
          href="https://github.com/kitao/pyxel"
          target="_blank"
          rel="noopener"
        >Made with Pyxel</a>
        <span class="creator-card__credit-sep">·</span>
        <span>Powered by <NuxtLink to="/">Code &amp; Magic</NuxtLink></span>
      </footer>
    </article>
  </div>
</template>

<style scoped>
/* WorkInfoDialog / MobileDialog と同じデザイン語彙:
   - 淡いピンクベージュの羊皮紙
   - 紺色のヘッダー帯 + 金線
   - Noto Sans JP 本文 / 明朝の見出し
   公開閲覧ページなので Code & Magic 固有装飾は控えめに保つ。 */

.creator-page {
  /* Header (v-app-bar) と Footer の高さを差し引いて、その間に最大 4:3 の
     カードを置く。`min-height` だと flex 親 (v-main) を成長させて landing
     ルートが 100dvh を超え、ボディスクロールが出てしまうため、`height` を
     固定 + `overflow: hidden` で抑える。 --hdr-h / --ftr-h は実測より少し
     余裕を持たせて、ヘッダー/フッターに被らないようにする。
     v-app-bar は `app` prop で fixed 配置になっており flow を占有しないため、
     `margin-top: var(--hdr-h)` でメインエリアをヘッダー下に押し出す
     (これをしないと card 上端が y=0 で固定ヘッダーの裏に潜り込む)。 */
  --page-pad: clamp(0.75rem, 2vh, 1.5rem);
  --hdr-h: 64px;
  --ftr-h: 60px;

  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: var(--hdr-h);
  height: calc(100vh - var(--hdr-h) - var(--ftr-h));
  height: calc(100dvh - var(--hdr-h) - var(--ftr-h));
  overflow: hidden;
  padding: var(--page-pad);
  box-sizing: border-box;
}

.creator-card {
  /* 4:3 fit-to-available — 横方向と縦方向のどちらかで頭打ちになる方に
     寸法を揃え、アスペクト比 4:3 を保ちつつ、ヘッダー/フッターに
     かぶらない最大サイズを取る。 */
  --avail-w: calc(100vw - 2 * var(--page-pad));
  --avail-h: calc(100dvh - var(--hdr-h) - var(--ftr-h) - 2 * var(--page-pad));
  width: min(var(--avail-w), calc(var(--avail-h) * 4 / 3));
  height: min(var(--avail-h), calc(var(--avail-w) * 3 / 4));
  max-width: 1200px;

  background: linear-gradient(135deg, #f5e8d6 0%, #ecdbc1 100%);
  border: 1px solid rgba(140, 100, 50, 0.35);
  border-radius: 4px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);

  font-family: "Noto Sans JP", sans-serif;
  color: #2a1f10;

  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* --- Head (navy ribbon + gold underline) — X プロフィール風に縦積み。
       名前 → @handle → 冒険になった日 → 外部リンク列 という順で表示。 --- */

.creator-card__head {
  flex: 0 0 auto;
  background: linear-gradient(135deg, #2c3e5a 0%, #1f2c44 100%);
  color: #f4e4bf;
  border-bottom: 2px solid #c9a440;
  padding: 0.8rem 1.5rem 0.55rem;
}

.creator-card__head-inner {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.creator-card__title-row {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 0.2rem 0.7rem;
}

.creator-card__name {
  font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", "Noto Serif", serif;
  font-weight: 700;
  font-size: clamp(1.3rem, 3vw, 1.7rem);
  margin: 0;
  line-height: 1.3;
  word-break: break-word;
}

/* 称号 (冒険者ランク) — 名前の右隣に小さく付け足す。
   link-chip と同系統の薄い枠で囲み、「ランクの肩書き」だと
   一目で分かるバッジ風の装飾を入れる。 */
.creator-card__title {
  display: inline-block;
  padding: 0.1rem 0.55rem;
  background: rgba(244, 228, 191, 0.08);
  border: 1px solid rgba(244, 228, 191, 0.4);
  border-radius: 3px;
  font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", "Noto Serif", serif;
  font-size: 0.85rem;
  font-weight: 500;
  line-height: 1.4;
  color: rgba(244, 228, 191, 0.85);
  letter-spacing: 0.03em;
  white-space: nowrap;
}

/* 編集ボタンは title-row の右端に寄せる (flex-wrap で折り返しても
   右端を維持)。 */
.creator-card__edit-btn {
  flex: 0 0 auto;
  margin-left: auto;
  color: #f4e4bf !important;
  border-color: rgba(244, 228, 191, 0.5) !important;
  letter-spacing: 0.05em;
}

/* --- Meta line: @handle · 冒険になった日 + 外部リンク群 を 1 行で --- */

.creator-card__meta {
  display: flex;
  flex-wrap: wrap;
  /* テキスト同士はベースライン揃え (monospace と Noto Sans JP の視覚的
     な高さ差を吸収)、リンク chip 群は中央揃えに自己整列させる。 */
  align-items: baseline;
  gap: 0.2rem 0.5rem;
  font-size: 0.8rem;
  line-height: 1.4;
  color: rgba(244, 228, 191, 0.75);
}

.creator-card__handle {
  font-family: "Google Sans Code", "IBM Plex Mono", monospace;
}

.creator-card__meta-sep {
  opacity: 0.5;
}

.creator-card__joined {
  color: rgba(244, 228, 191, 0.65);
}

/* --- External link chips (header 内) ---
   meta 行の右端へ寄せる。狭いビューポートでは flex-wrap で折り返す。
   meta 親が baseline 揃えのため、chip 群だけは align-self で中央揃えに
   戻す (chip は中身が中央揃えなので baseline と齟齬を起こすため)。 */
.creator-card__links {
  display: flex;
  flex-wrap: wrap;
  align-self: center;
  gap: 0.3rem;
  margin-left: auto;
}

.link-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 0.4rem;
  min-width: 1.3rem;
  height: 1.3rem;
  background: rgba(244, 228, 191, 0.08);
  border: 1px solid rgba(244, 228, 191, 0.3);
  border-radius: 4px;
  color: rgba(244, 228, 191, 0.9);
  text-decoration: none;
  font-size: 0.7rem;
  letter-spacing: 0.02em;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.link-chip:hover {
  background: rgba(244, 228, 191, 0.18);
  border-color: rgba(244, 228, 191, 0.6);
  color: #f4e4bf;
}

.link-chip__icon {
  width: 12px;
  height: 12px;
}

/* Vuetify の v-icon 用にサイズを揃える */
.link-chip__icon.v-icon {
  font-size: 12px;
  width: 12px;
  height: 12px;
}

.link-chip__text {
  font-weight: 600;
}

/* ブランド色のニュアンス (chip ホバーで色が薄く乗る) */
.link-chip--itch:hover {
  color: #fa5c5c;
  border-color: rgba(250, 92, 92, 0.7);
}

.link-chip--pixiv:hover {
  color: #0096fa;
  border-color: rgba(0, 150, 250, 0.7);
}

/* --- Body — フォーム/作品グリッドはここに収まる。カードが画面サイズを
       超えるときは body 内部だけがスクロールする。 --- */

.creator-card__body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 1.25rem 1.5rem 1.5rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(140, 100, 50, 0.5) transparent;
}

/* bio 部分のレンダリングは CreatorBio コンポーネントに移譲 (#417)。 */
.creator-card__bio {
  margin-bottom: 1.25rem;
}

.bio-empty {
  font-size: 0.9rem;
  color: rgba(60, 40, 20, 0.6);
  font-style: italic;
  text-align: center;
  padding: 1rem 0;
  margin: 0 0 1rem;
}

/* --- Works --- */

.works {
  margin-top: 1.25rem;
  padding-top: 1.25rem;
  border-top: 1px solid rgba(140, 100, 50, 0.3);
}

.works__heading {
  font-family: "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif JP", "Noto Serif", serif;
  font-weight: 700;
  font-size: 1.05rem;
  color: #1f3554;
  margin: 0 0 1rem;
}

.works__empty {
  color: rgba(60, 40, 20, 0.55);
  font-style: italic;
  text-align: center;
  padding: 1.5rem 0;
}

/* #670 Phase 1: 他ページと数値を揃える (minmax 280px, gap 12px)。
   align-items: start は cradle 等で行高差が伝播しないようにする保険。 */
.works__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  align-items: start;
  gap: 12px;
}

/* --- Foot --- */

.creator-card__foot {
  flex: 0 0 auto;
  padding: 0.75rem 1.5rem 1rem;
  background: rgba(44, 62, 90, 0.05);
  border-top: 1px solid rgba(140, 100, 50, 0.3);
  font-size: 0.75rem;
  color: rgba(60, 40, 20, 0.55);
  text-align: center;
  letter-spacing: 0.02em;
}

.creator-card__credit-link {
  color: inherit;
  text-decoration: none;
  border-bottom: 1px dotted rgba(60, 40, 20, 0.4);
}

.creator-card__credit-link:hover {
  color: #1f3554;
  border-bottom-color: #1f3554;
}

.creator-card__credit-sep {
  margin: 0 0.6em;
  opacity: 0.6;
}

.creator-card__foot :deep(a) {
  color: inherit;
}

/* --- Mobile (≤ 600px) ---
   4:3 カードの中央配置を解除し、ページ全体を縦スクロールに切り替える。
   羊皮紙背景は body 全面まで広げ、カード枠 (border / radius / shadow) を
   落として full-bleed にする。WorkInfoDialog (MobileDialog) と同じ「上に
   紺リボン、下に羊皮紙」の語彙のまま、寸法だけスマホに寄せる。 */
@media (max-width: 600px) {
  .creator-page {
    display: block;
    height: auto;
    overflow: visible;
    /* v-app-bar (透明・app prop で fixed) を回避するため、モバイルでは
       padding で content を送り出す (デスクトップ側で使う margin-top は
       打ち消し、padding-top の var(--hdr-h) 一本に揃える)。 */
    margin-top: 0;
    padding: var(--hdr-h) 0 0;
  }

  .creator-card {
    width: 100%;
    height: auto;
    max-width: none;
    border: none;
    border-radius: 0;
    box-shadow: none;
  }

  .creator-card__head {
    padding: 0.7rem 1rem 0.55rem;
  }

  .creator-card__title-row {
    gap: 0.5rem;
  }

  /* モバイルでは編集導線 (= 冒険者のキャンプ経由) がそもそも開けないので、
     編集ボタンを非表示にする。本人が PC で開き直したときに編集する想定。 */
  .creator-card__edit-btn {
    display: none;
  }

  .creator-card__meta {
    gap: 0.2rem 0.4rem;
  }

  /* デスクトップでは外部リンクを meta 行の右端に寄せていたが、狭幅では
     折り返した先で位置が浮いて見えるので、左揃え (行頭から並ぶ) に戻す。 */
  .creator-card__links {
    margin-left: 0;
    flex-basis: 100%;
    margin-top: 0.15rem;
  }

  /* body 内スクロールは止め、ページ全体でスクロールさせる。 */
  .creator-card__body {
    overflow: visible;
    padding: 1rem 1rem 1.25rem;
  }

  .works {
    margin-top: 1rem;
    padding-top: 1rem;
  }

  /* スマホ幅では 280px minmax が中途半端 (1 列だが横余白が出る) になる
     ので、明示的に 1 カラム固定にする。 */
  .works__grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  /* #670 Phase 3 以降: WorkCard は内部で `--cu` (= card 幅基準の
     container query 単位) でサイジングするようになり、モバイル幅
     1fr で card が広がれば文字も自然に大きくなる。以前の
     `.works { --u: clamp(...) }` 上書きはこの変更で不要になった。 */

  .creator-card__foot {
    padding: 0.7rem 1rem 0.85rem;
  }
}
</style>
